// Página pública da Política de Privacidade
// Conteúdo voltado ao usuário final (sem notas internas de revisão jurídica).
import React from "react";
import LegalDocument from "components/LegalDocument";

const CONTENT = `
## 1. Introdução

A sua privacidade é importante para nós. Esta Política de Privacidade explica **como coletamos, usamos, armazenamos, compartilhamos e protegemos** os seus dados pessoais quando você utiliza o **TiqChat** (www.tiqchat.com), em conformidade com a **Lei Geral de Proteção de Dados Pessoais (LGPD — Lei nº 13.709/2018)**.

Ao usar o TiqChat, você declara estar ciente desta Política. Recomendamos a leitura atenta deste documento, em conjunto com os nossos [Termos de Uso](/termos-de-uso).

## 2. Controlador dos dados

O **controlador** dos seus dados pessoais — ou seja, a empresa responsável pelas decisões sobre o tratamento dos seus dados — é:

- **Razão social:** Stonel19 Soluções em IA Gen Ltda
- **Nome fantasia:** Stonel19
- **CNPJ:** 61.577.764/0001-39
- **Endereço:** Rua Profª. Cremilde de Oliveira, 328 — Jardim Atlântico, Florianópolis/SC — CEP 88.095-560

## 3. Encarregado de Proteção de Dados (DPO)

Em cumprimento ao art. 41 da LGPD, indicamos o nosso **Encarregado de Proteção de Dados** (Data Protection Officer — DPO), responsável por receber comunicações e atender solicitações relacionadas aos seus dados pessoais:

- **Nome:** Faglimar Lemos de Oliveira
- **E-mail:** faglimar@stonel19.com

Você pode contatar o Encarregado a qualquer momento para exercer seus direitos ou esclarecer dúvidas sobre o tratamento dos seus dados.

## 4. Quais dados pessoais coletamos

Coletamos diferentes categorias de dados, conforme a sua interação com a Plataforma:

### 4.1 Dados que você nos fornece diretamente
- **Dados de cadastro:** endereço de e-mail e senha (a senha é armazenada de forma criptografada).
- **Dados de perfil:** nome ou apelido de exibição que você escolher.
- **Dados de pagamento:** ao assinar um plano pago, os dados do cartão são coletados e processados diretamente pela **Cielo S.A.** Não armazenamos os números completos do seu cartão.
- **Conteúdo das conversas:** as mensagens que você envia aos personagens.

### 4.2 Dados coletados automaticamente
- **Dados de uso e navegação:** páginas acessadas, recursos utilizados, data e hora de acesso, contagem de mensagens.
- **Dados técnicos:** tipo de dispositivo, navegador, sistema operacional e endereço IP.
- **Cookies e identificadores:** ver a Seção 11 (Cookies).

### 4.3 Dados de áudio
Quando você usa o recurso de **gravação de voz**, o áudio é enviado para transcrição automática (conversão de fala em texto) por meio do serviço **Groq (Whisper)**. O áudio é processado para gerar o texto da sua mensagem e não é armazenado de forma permanente por nós.

### 4.4 Dados que NÃO coletamos intencionalmente
Não solicitamos dados sensíveis (como religião, saúde, orientação sexual etc.). Embora a temática seja bíblica, **não exigimos** que você informe sua religião. Pedimos que você **não insira** dados pessoais sensíveis (seus ou de terceiros) nas conversas.

## 5. Como coletamos os seus dados

Coletamos dados:
- Quando você **se cadastra** e cria uma conta;
- Quando você **conversa** com os personagens;
- Quando você **assina um plano** pago;
- Automaticamente, por meio de **cookies** e ferramentas de análise, enquanto você navega.

## 6. Para que usamos os seus dados (finalidades e bases legais)

De acordo com a LGPD, todo tratamento de dados tem uma **finalidade específica** e uma **base legal**. Veja como tratamos seus dados:

| Finalidade | Dados utilizados | Base legal (LGPD) |
|---|---|---|
| Criar e gerenciar sua conta | E-mail, senha, perfil | Execução de contrato (art. 7º, V) |
| Permitir as conversas com os personagens | Conteúdo das mensagens, áudio | Execução de contrato (art. 7º, V) |
| Processar pagamentos e assinaturas | Dados de pagamento | Execução de contrato (art. 7º, V) |
| Aplicar limites de uso (plano gratuito) | Dados de uso, identificador | Execução de contrato (art. 7º, V) |
| Melhorar a Plataforma e analisar uso | Dados de navegação, cookies | Legítimo interesse (art. 7º, IX) |
| Enviar comunicações sobre o serviço | E-mail | Execução de contrato / Legítimo interesse |
| Marketing e remarketing (anúncios) | Cookies, dados de navegação | Consentimento (art. 7º, I) |
| Cumprir obrigações legais | Dados cadastrais e de transação | Obrigação legal (art. 7º, II) |
| Prevenir fraudes e garantir segurança | Dados técnicos, IP | Legítimo interesse (art. 7º, IX) |

## 7. Com quem compartilhamos os seus dados

Não vendemos seus dados pessoais. Compartilhamos dados apenas com **parceiros e fornecedores essenciais** para o funcionamento do TiqChat, sempre com a devida proteção contratual:

| Parceiro | Finalidade | O que recebe |
|---|---|---|
| **Cielo S.A.** | Processamento de pagamentos | Dados de pagamento e transação |
| **n8n (Railway)** | Processamento das conversas com IA | Conteúdo das mensagens, ID do usuário |
| **Groq (Whisper)** | Transcrição de áudio em texto | Áudio enviado pelo usuário |
| **Google Analytics** | Análise de uso e audiência | Dados de navegação e cookies |
| **Meta Pixel** | Mensuração e anúncios | Dados de navegação e cookies |

Também poderemos compartilhar dados:
- **Por exigência legal ou judicial**, mediante ordem de autoridade competente;
- Em caso de **reorganização societária** (fusão, aquisição), mantendo as proteções desta Política.

### 7.1 Transferência internacional de dados
Alguns de nossos fornecedores (como Groq, Google e Meta) podem processar dados em **servidores localizados fora do Brasil**. Nesses casos, adotamos as salvaguardas previstas na LGPD (art. 33) para garantir um nível adequado de proteção dos seus dados.

## 8. Onde e como armazenamos os dados

### 8.1 Histórico de conversas — armazenamento local
O **histórico das suas conversas** com os personagens é armazenado **exclusivamente no seu próprio dispositivo** (no armazenamento local do navegador — *localStorage*). **Nós não guardamos o seu histórico de conversas em nossos servidores.**

Isso significa que:
- Suas conversas ficam no aparelho que você usa;
- Se você **limpar os dados do navegador** ou **trocar de aparelho**, o histórico **não é transferido**;
- No plano gratuito, o histórico local é mantido por até **2 dias**; no plano Premium, é mantido sem prazo definido (enquanto permanecer no dispositivo).

### 8.2 Dados de cadastro e conta
Os dados de cadastro (e-mail, perfil, status de assinatura) são armazenados em servidores seguros, pelo tempo necessário às finalidades descritas.

## 9. Por quanto tempo guardamos os seus dados

Mantemos seus dados pessoais pelo tempo necessário para cumprir as finalidades desta Política, observando:
- **Dados de cadastro:** enquanto sua conta estiver ativa e por prazo razoável após o encerramento, para fins legais;
- **Dados de transações/pagamento:** pelo prazo exigido pela legislação fiscal e civil (em regra, até 5 anos);
- **Histórico de conversas:** conforme Seção 8.1 (armazenamento local no dispositivo do usuário);
- **Dados para cumprimento de obrigação legal:** pelo prazo estabelecido na respectiva lei.

Após esses prazos, os dados são eliminados ou anonimizados, salvo quando a guarda for autorizada ou exigida por lei (art. 16 da LGPD).

## 10. Seus direitos como titular dos dados

A LGPD garante a você, como titular dos dados, os seguintes direitos (art. 18):

1. **Confirmação** da existência de tratamento dos seus dados;
2. **Acesso** aos seus dados;
3. **Correção** de dados incompletos, inexatos ou desatualizados;
4. **Anonimização, bloqueio ou eliminação** de dados desnecessários ou tratados em desconformidade com a lei;
5. **Portabilidade** dos dados a outro fornecedor, mediante requisição;
6. **Eliminação** dos dados tratados com base no seu consentimento;
7. **Informação** sobre as entidades com as quais compartilhamos seus dados;
8. **Informação** sobre a possibilidade de não fornecer consentimento e suas consequências;
9. **Revogação do consentimento**, a qualquer momento.

### 10.1 Como exercer seus direitos
Para exercer qualquer um desses direitos, entre em contato com o nosso Encarregado de Dados:
- **E-mail:** faglimar@stonel19.com

Responderemos à sua solicitação no menor prazo possível, observados os prazos e procedimentos da LGPD. Poderemos solicitar informações adicionais para confirmar a sua identidade antes de atender ao pedido.

## 11. Cookies e tecnologias de rastreamento

O TiqChat utiliza **cookies** e tecnologias similares para funcionamento, análise e marketing.

### 11.1 Tipos de cookies que usamos
- **Cookies essenciais:** necessários para o funcionamento da Plataforma (ex.: manter você logado).
- **Cookies de análise:** **Google Analytics**, para entender como a Plataforma é usada e melhorá-la.
- **Cookies de marketing:** **Meta Pixel**, para mensurar campanhas e exibir anúncios relevantes.

### 11.2 Gerenciamento de cookies
Você pode aceitar ou recusar cookies não essenciais por meio do aviso de cookies exibido ao acessar a Plataforma, e também gerenciá-los nas configurações do seu navegador. A desativação de alguns cookies pode afetar o funcionamento da Plataforma.

## 12. Segurança dos dados

Adotamos medidas técnicas e organizacionais para proteger seus dados contra acesso não autorizado, perda, alteração ou divulgação indevida, tais como:
- Criptografia de senhas;
- Conexão segura (HTTPS);
- Controle de acesso aos sistemas;
- Seleção de fornecedores que adotam padrões de segurança.

Apesar dos nossos esforços, **nenhum sistema é 100% seguro**. Em caso de incidente de segurança que possa acarretar risco relevante aos titulares, comunicaremos os afetados e a Autoridade Nacional de Proteção de Dados (ANPD), conforme exigido pela LGPD (art. 48).

## 13. Crianças e adolescentes

O TiqChat é destinado **exclusivamente a maiores de 18 anos**. Não coletamos intencionalmente dados de menores de 18 anos. Caso identifiquemos o cadastro de um menor, tomaremos medidas para encerrar a conta e excluir os dados. Se você é responsável por um menor e acredita que ele nos forneceu dados, contate-nos imediatamente.

## 14. Alterações nesta Política

Podemos atualizar esta Política de Privacidade periodicamente, para refletir mudanças legais, técnicas ou de negócio. Alterações relevantes serão comunicadas pela Plataforma ou por e-mail. A data de "última atualização" no topo indica a versão vigente.

## 15. Autoridade Nacional e legislação aplicável

Esta Política é regida pela legislação brasileira, em especial a **LGPD (Lei nº 13.709/2018)**. Caso você entenda que seus direitos não foram adequadamente atendidos, pode também recorrer à **Autoridade Nacional de Proteção de Dados (ANPD)**.

## 16. Contato

Para dúvidas, solicitações ou reclamações sobre esta Política de Privacidade ou sobre o tratamento dos seus dados:

- **Encarregado de Dados (DPO):** Faglimar Lemos de Oliveira
- **E-mail:** faglimar@stonel19.com
- **WhatsApp:** (16) 98202-6554
- **Endereço:** Rua Profª. Cremilde de Oliveira, 328 — Jardim Atlântico, Florianópolis/SC — CEP 88.095-560
`;

export default function PrivacyPolicy() {
    return (
        <LegalDocument
            title="Política de Privacidade"
            lastUpdate="18 de junho de 2026"
            markdown={CONTENT}
        />
    );
}
